import { useEffect, useRef } from 'react';

/**
 * Signs the user out after a period without activity (VAPT "Improper Session
 * Expiration"), with a warning first.
 *
 * Activity is recorded as a timestamp in localStorage, shared by every tab of the
 * dashboard: the user is idle only when NO tab has seen activity for `timeoutMs`.
 * (react-idle-timer's cross-tab mode was not used: an untouched tab going idle
 * signed out the tab being worked in.) The timestamp is not user data — just "the
 * last time anyone touched this browser's dashboard".
 *
 * Only real input counts. `scroll` is deliberately not listened to: the app scrolls
 * on its own (a chat window following new messages), which would keep an unattended
 * screen signed in forever. Wheel, touch and keys cover a person scrolling.
 */
const LAST_ACTIVITY_KEY = 'quikstep-last-activity';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'touchmove', 'pointerdown'];
const RECORD_EVERY_MS = 1000;
const CHECK_EVERY_MS = 5000;

const now = () => Date.now();

const readLastActivity = (): number => {
  try {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
};

/** Record activity now (also used by "Stay signed in"). */
export const markActivity = () => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now()));
  } catch {
    // Storage unavailable: this tab still tracks activity through its own clock below.
  }
};

// Things that keep the user busy without any input — a connected video call. While
// any registered check returns true, the user counts as active in every tab.
const busyChecks = new Set<() => boolean>();

/** Count the user as active while `isBusy()` is true. Returns a function that stops it. */
export const registerBusyCheck = (isBusy: () => boolean): (() => void) => {
  busyChecks.add(isBusy);
  return () => {
    busyChecks.delete(isBusy);
  };
};

const isBusy = () =>
  Array.from(busyChecks).some((check) => {
    try {
      return check();
    } catch {
      return false;
    }
  });

/** Real input from a person — not a scripted event, nor the zero-distance mousemove a browser fires when content moves under a still cursor. */
const isRealInput = (event: Event) => {
  if (!event.isTrusted) return false;
  if (event.type === 'mousemove') {
    const { movementX, movementY } = event as MouseEvent;
    return movementX !== 0 || movementY !== 0;
  }
  return true;
};

interface IdleSignOutOptions {
  timeoutMs: number;
  warningMs: number;
  isSignedIn: () => boolean;
  onWarning: () => void;
  onActiveAgain: () => void;
  onIdle: () => void;
}

export const useIdleSignOut = ({ timeoutMs, warningMs, isSignedIn, onWarning, onActiveAgain, onIdle }: IdleSignOutOptions) => {
  // Latest callbacks without re-subscribing listeners on every render.
  const handlers = useRef({ isSignedIn, onWarning, onActiveAgain, onIdle });
  handlers.current = { isSignedIn, onWarning, onActiveAgain, onIdle };

  useEffect(() => {
    let ownLastActivity = now();
    let lastRecorded = 0;
    let warned = false;
    let signedOut = false;

    const recordActivity = () => {
      ownLastActivity = now();
      if (ownLastActivity - lastRecorded >= RECORD_EVERY_MS) {
        lastRecorded = ownLastActivity;
        markActivity();
      }
    };

    const onInput = (event: Event) => {
      if (isRealInput(event)) recordActivity();
    };

    const check = () => {
      if (signedOut || !handlers.current.isSignedIn()) {
        warned = false;
        return;
      }
      if (isBusy()) recordActivity();
      const idleFor = now() - Math.max(ownLastActivity, readLastActivity());
      if (idleFor >= timeoutMs) {
        signedOut = true;
        handlers.current.onIdle();
      } else if (idleFor >= timeoutMs - warningMs) {
        if (!warned) {
          warned = true;
          handlers.current.onWarning();
        }
      } else if (warned) {
        warned = false;
        handlers.current.onActiveAgain();
      }
    };

    // A tab coming back from the background (or a phone waking up) checks at once.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    markActivity();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onInput, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onInput, { capture: true }));
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [timeoutMs, warningMs]);
};
